import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfo,
  ContactInfoInline,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 8 - 우아한 그린 액센트 디자인
const Template8 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-8 resume-container bg-white p-8 ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl font-light mb-2 text-gray-800", "text-4xl font-light mb-2 text-gray-300 italic")}>
          {currentData.name}
        </h1>
        <p className={getPlaceholderClass(hasRealData.title, "text-lg uppercase tracking-widest text-gray-700", "text-lg uppercase tracking-widest text-gray-300 italic")}>
          {currentData.title}
        </p>
      </div>

      {/* Contact Info */}
      <section className="mb-8">
        <ContactInfoInline
          phone={currentData.phone}
          email={currentData.email}
          address={currentData.address}
          socialLinks={currentData.socialLinks}
          hasRealData={hasRealData}
          className="justify-center"
          separator=" | "
          showIcons={false}
          textColor="text-gray-600"
        />
      </section>

      {/* Professional Summary */}
      {currentData.summary && (
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
            {titles.professionalSummary || titles.introduction}
          </h2>
          <div className="border-t border-gray-300 mb-4"></div>
          <SummarySection
            summary={currentData.summary}
            hasRealData={hasRealData}
            textClassName="text-sm leading-relaxed text-gray-700"
          />
        </section>
      )}

      {/* Core Competencies */}
      {currentData.keyStrengths.length > 0 && (
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
            {titles.coreCompetencies}
          </h2>
          <div className="border-t border-gray-300 mb-4"></div>
          <CoreCompetenciesSection
            keyStrengths={currentData.keyStrengths}
            hasRealData={hasRealData}
            className="space-y-1"
            itemClassName="text-sm"
            bulletStyle="•"
            bulletClassName="text-gray-700"
            textClassName="text-gray-700"
          />
        </section>
      )}

      {/* Research Experience / Work Experience */}
      <section className="mb-8">
        <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
          {titles.researchExperience || titles.experience}
        </h2>
        <div className="border-t border-gray-300 mb-4"></div>
        <ExperienceSection
          experiences={currentData.experiences}
          hasRealData={hasRealData}
          itemClassName="mb-6 last:mb-0"
          datePosition="right"
          positionClassName="font-semibold text-gray-800"
          companyClassName="text-sm text-gray-700 italic"
          dateClassName="text-sm text-gray-600"
          descriptionClassName="text-sm mt-2"
          projectTitleClassName="text-sm font-semibold text-gray-700"
          projectDescriptionClassName="text-sm mt-1"
          projectIndent="pl-4"
          bulletStyle="•"
        />
      </section>

      {/* Education */}
      <section className="mb-8">
        <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
          {titles.education}
        </h2>
        <div className="border-t border-gray-300 mb-4"></div>
        <EducationSection
          education={currentData.education}
          hasRealData={hasRealData}
          itemClassName="mb-4 last:mb-0"
          datePosition="right"
          degreeClassName="font-semibold text-gray-800"
          schoolClassName="text-sm text-gray-700 italic"
          majorClassName="text-sm text-gray-600"
          dateClassName="text-sm text-gray-600"
          showMajor={true}
        />
      </section>

      {/* Certificates */}
      {currentData.certificates.length > 0 && (
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
            {titles.certificates}
          </h2>
          <div className="border-t border-gray-300 mb-4"></div>
          <CertificateSection
            certificates={currentData.certificates}
            hasRealData={hasRealData}
            itemClassName="mb-3 last:mb-0"
            nameClassName="text-sm font-semibold text-gray-800"
            issuerClassName="text-sm text-gray-600 italic"
            dateClassName="text-sm text-gray-600"
            dateFormat="separate"
          />
        </section>
      )}

      {/* Skills */}
      <section className="mb-8">
        <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
          {titles.skills}
        </h2>
        <div className="border-t border-gray-300 mb-4"></div>
        <SkillSection
          skills={currentData.skills}
          hasRealData={hasRealData}
          variant="inline"
          className="text-sm text-gray-700"
        />
      </section>

      {/* Notable Awards */}
      {hasRealData.papers && (
        <section className="mb-8">
          <h2 className="text-base font-bold uppercase tracking-wider text-green-700 mb-3">
            {titles.notableAwards || titles.papers}
          </h2>
          <div className="border-t border-gray-300 mb-4"></div>
          <PaperSection
            papers={currentData.papers}
            hasRealData={hasRealData}
            getPaperTypeText={getPaperTypeText}
            itemClassName="mb-3 last:mb-0"
            titleClassName="text-sm font-semibold text-gray-800"
            authorsClassName="text-sm text-gray-600"
            journalClassName="text-sm text-gray-600 italic"
            typeClassName="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded"
          />
        </section>
      )}
    </div>
  );
};

export default Template8;